import type React from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePageTitle } from '@/hooks/usePageTitle';
import { ClosingBand, PageHero, RequestAccessButton, TextLink } from '@/components/marketing/Sections';
import { getPlanAnnualTotal, getPlanPrice, type EntityType } from '@showliner/shared/constants/pricing';
import { PLAN_LIMITS } from '@showliner/shared/types/PlanFeatures';

/* ==========================================================================
   PRICING. A printed price list: three rows, no highlighted middle card.

   Prices come from packages/shared/src/constants/pricing.ts and every number
   in a feature line comes from PLAN_LIMITS, because the hand-typed version of
   this page drifted from the code in six places (Business sold as "Talk to
   us" while it was self-serve, the wrong stage plot and team counts, and
   features that were never built). A line that isn't a number is a shipped
   feature, checked by hand; if it isn't built, it isn't here.
   ========================================================================== */

type Tab = 'venue' | 'performer';

type Plan = {
  name: string;
  /** Monthly price in dollars; null is free. */
  monthly: number | null;
  annualTotal: number | null;
  blurb: string;
  features: string[];
};

const free = PLAN_LIMITS.free;
const pro = PLAN_LIMITS.pro;

const price = (entity: EntityType, plan: 'pro' | 'business') => ({
  monthly: getPlanPrice(entity, plan, 'monthly'),
  annualTotal: getPlanAnnualTotal(entity, plan),
});

const plans: Record<Tab, Plan[]> = {
  venue: [
    {
      name: 'Free',
      monthly: null,
      annualTotal: null,
      blurb: 'For a room that books a few shows a month.',
      features: [
        `${free.maxConfirmedBookingsPerMonth} confirmed shows a month`,
        `${free.maxCollaborativeEventsPerMonth} shows a month with other Showliner accounts on the bill (acts who aren't on Showliner don't count)`,
        `${free.maxActiveRooms} live room at a time, with the TV display, QR code and chat`,
        `${free.maxStagePlots} stage plots`,
        `${free.maxTeamMembers} people on your team`,
        'Run your shows from ChatGPT, Claude and other AI assistants',
      ],
    },
    {
      name: 'Pro',
      ...price('venue', 'pro'),
      blurb: 'For a room running shows every week.',
      features: [
        'Unlimited confirmed shows',
        'Weekly or monthly nights that repeat on their own',
        'Festivals: several stages or days, with a lineup you reveal when you choose',
        `${pro.maxCollaborativeEventsPerMonth} shows a month with other Showliner accounts on the bill`,
        `${pro.maxActiveRooms} live rooms at a time`,
        'Your own slides and loops on the TV between sets',
        'Unlimited stage plots',
        `${pro.maxCustomEmotes} custom emotes for your room's chat`,
        `${pro.maxTeamMembers} people on your team, and up to ${pro.maxOwnedWorkspaces} workspaces`,
        'One calendar across all your workspaces',
      ],
    },
    {
      name: 'Business',
      ...price('venue', 'business'),
      blurb: 'For a group running several rooms.',
      features: [
        'Everything in Pro',
        'Unlimited live rooms, workspaces and team members',
        'Unlimited shows with other Showliner accounts on the bill',
      ],
    },
  ],
  performer: [
    {
      name: 'Free',
      monthly: null,
      annualTotal: null,
      blurb: 'For artists booking a few of their own shows, and for crew.',
      features: [
        'Answer any invite, offer or advance a venue sends you, which never counts toward a limit',
        'Your page, a short link and a QR code for the merch table',
        'Tips and song requests from the crowd',
        `${free.maxStagePlots} stage plots`,
        `${free.maxConfirmedBookingsPerMonth} confirmed shows a month that you book yourself`,
        `${free.maxCollaborativeEventsPerMonth} shows a month you organize with other Showliner accounts on the bill`,
        `${free.maxTeamMembers} people on your team`,
        'Run your shows from ChatGPT, Claude and other AI assistants',
      ],
    },
    {
      name: 'Pro',
      ...price('performer', 'pro'),
      blurb: 'For artists booking a lot of their own shows.',
      features: [
        'Unlimited confirmed shows that you book yourself',
        'Weekly or monthly nights that repeat on their own',
        `${pro.maxCollaborativeEventsPerMonth} shows a month you organize with other Showliner accounts on the bill`,
        'Unlimited stage plots',
        `${pro.maxCustomEmotes} custom emotes for your fans`,
        `${pro.maxTeamMembers} people on your team, and up to ${pro.maxOwnedWorkspaces} workspaces`,
        'One calendar across all your workspaces',
      ],
    },
    {
      name: 'Business',
      ...price('performer', 'business'),
      blurb: 'For a manager with several acts.',
      features: [
        'Everything in Pro',
        'Unlimited workspaces, one for each act',
        'Unlimited team members and live rooms',
      ],
    },
  ],
};

const faqs: { q: string; a: React.ReactNode }[] = [
  {
    q: 'How do I get access?',
    a: 'Request access and tell us about your room. We\'re opening Showliner to a few venues at a time while it\'s early. Acts and crew who get a link from a venue can sign up from that link straight away, and fans can make an account whenever they like.',
  },
  {
    q: 'What counts as a confirmed show?',
    a: 'A show you booked where every act you\'ve told about it has agreed to play. Pencilling acts in, sending offers and waiting on answers don\'t count, and neither does answering a booking somebody else sent you.',
  },
  {
    q: 'Does it cost the acts anything?',
    a: 'No. Anyone you book answers the invite, the offer, the advance and the settlement for free, without an account. Performer Pro is for artists who book a lot of their own shows.',
  },
  {
    q: 'I\'m a promoter, not a venue. Which plan?',
    a: 'The venue plans. Everything works the same whether you own the room or you\'re renting it for the night.',
  },
  {
    q: 'Do you take a cut of tips or the door?',
    a: 'No. The act keeps the whole tip, and the fan covers the card fee by default. Fans can add a little extra to keep the lights on here if they want to. Showliner never handles the money between a venue and an act.',
  },
  {
    q: 'Do I need to buy any hardware?',
    a: 'No. The room\'s display runs in a web browser on whatever is connected to your TV, and fans use their own phones.',
  },
  {
    q: 'Can I cancel?',
    a: 'Any time, from the billing page in your workspace. There\'s no contract.',
  },
  {
    q: 'Is there annual billing?',
    a: 'Yes. Paying for a year costs ten months, and you choose monthly or annual when you upgrade.',
  },
  {
    q: 'I book shows and I\'m in a band. Do I need two accounts?',
    a: 'No. One account can hold both, and you switch between them. On Free you own one workspace, and Pro lets you own up to three.',
  },
];

const PlanPrice: React.FC<{ plan: Plan }> = ({ plan }) =>
  plan.monthly === null ? (
    <div className="mt-4 flex items-baseline gap-1.5">
      <span className="font-display text-4xl text-ink leading-none">$0</span>
    </div>
  ) : (
    <>
      <div className="mt-4 flex items-baseline gap-1.5">
        <span className="font-display text-4xl text-ink leading-none">${plan.monthly}</span>
        <span className="text-sm text-grey-600 font-mono">/mo</span>
      </div>
      {plan.annualTotal !== null && (
        <p className="mt-1.5 text-sm text-grey-600">or ${plan.annualTotal} a year</p>
      )}
    </>
  );

const PricingPage: React.FC = () => {
  usePageTitle('Pricing');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get('for') === 'performers' ? 'performer' : 'venue';

  const setTab = (next: Tab) => {
    setSearchParams(next === 'performer' ? { for: 'performers' } : {}, { replace: true });
  };

  return (
    <>
      <PageHero title="Pricing">
        <p>
          Free for a venue or an artist booking a few shows a month. Anyone you book answers you
          for free and never hits a limit.
        </p>
      </PageHero>

      <section>
        <div className="mx-auto max-w-6xl px-4 sm:px-8 pb-8">
          <div className="inline-flex border border-grey-300 bg-grey-50 p-0.5 rounded-md" role="group" aria-label="Plans for">
            {(
              [
                ['venue', 'Venues and promoters'],
                ['performer', 'Artists and crew'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={tab === value}
                onClick={() => setTab(value)}
                className={cn(
                  'px-5 py-2 text-sm font-medium transition-colors rounded-[5px]',
                  tab === value ? 'bg-ink text-grey-50' : 'text-grey-700 hover:text-ink',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-grey-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          {plans[tab].map((plan) => (
            <article
              key={plan.name}
              className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-6 py-10 sm:py-12 border-b border-grey-200 last:border-b-0"
            >
              <div className="md:col-span-3">
                <h2 className="font-display text-3xl text-ink leading-none tracking-tight">{plan.name}</h2>
                <PlanPrice plan={plan} />
                <p className="mt-3 text-sm text-grey-700 leading-snug max-w-[28ch]">{plan.blurb}</p>
              </div>

              <ul className="md:col-span-7 space-y-1.5 self-center">
                {plan.features.map((f) => (
                  <li key={f} className="grid grid-cols-[1.25rem_1fr] gap-2 text-base text-grey-800 leading-relaxed">
                    <span className="text-grey-400 font-mono pt-0.5" aria-hidden="true">+</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="md:col-span-2 md:flex md:items-center md:justify-end">
                {plan.name === 'Free' ? <RequestAccessButton /> : null}
              </div>
            </article>
          ))}
          <p className="py-6 text-sm text-grey-600 max-w-[70ch]">
            Paid plans start from inside Showliner, once your workspace is set up. Prices are in US
            dollars.
          </p>
        </div>
      </section>

      <section className="border-t border-grey-200 bg-grey-100/60">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 section">
          <h2 className="font-display text-3xl text-ink leading-none tracking-tight mb-10">Questions</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
            {faqs.map((faq) => (
              <div key={faq.q}>
                <dt className="text-base font-semibold text-ink leading-snug">{faq.q}</dt>
                <dd className="mt-2 text-sm text-grey-700 leading-relaxed">{faq.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <ClosingBand
        title="Start on Free"
        actions={
          <>
            <RequestAccessButton />
            <TextLink to="mailto:hello@showliner.io">Ask us something first</TextLink>
          </>
        }
      >
        <p>Move up when a limit gets in the way, and drop back down whenever you want.</p>
      </ClosingBand>
    </>
  );
};

export default PricingPage;
